import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ClipboardList,
  Clock,
  FileCheck,
  FlaskConical,
  Mail,
  Phone,
  ShieldCheck,
  Truck,
} from "lucide-react";
import Seo from "@/components/Seo";

const Fornecedores = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Fornecedores Quimical: cadastro e parcerias"
        description="Informações para fornecedores enviarem cotações, critérios de homologação e contato comercial da Quimical."
        canonical="https://quimical.com.br/fornecedores"
      />
      <Header />

      <main className="pt-24">
        <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <Badge className="px-4 py-1">Parcerias estratégicas</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Fornecedores
              </h1>
              <p className="text-lg text-muted-foreground">
                Trabalhamos com transportadores e fabricantes de produtos químicos que compartilham nosso compromisso com segurança, qualidade e eficiência. Conheça como enviar suas cotações e os passos para se tornar um parceiro homologado da Quimical.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-soft">
                  <a href="mailto:fornecedores@quimical.com.br?subject=Cota%C3%A7%C3%A3o%20de%20fornecedor">
                    Enviar cotação
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10">
                  <a href="#cadastro">Instruções de cadastro</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto space-y-12">
              <div className="space-y-4 text-center">
                <h2 className="text-3xl font-bold text-foreground">
                  Como enviar sua cotação
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Nosso time comercial atende propostas para suprimentos industriais, insumos químicos, embalagens e soluções logísticas. Para garantir uma análise rápida, inclua o máximo de informações possíveis logo no primeiro contato.
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-2">
                <Card className="border-2 hover:border-primary/30 transition-colors">
                  <CardHeader className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle>Envio eletrônico</CardTitle>
                        <CardDescription>
                          Encaminhe sua proposta diretamente para nosso time.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 text-left">
                    <div className="flex gap-3">
                      <ClipboardList className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Utilize o assunto <strong>“Cotação de fornecedor”</strong> e descreva o produto/serviço, condições comerciais e prazos de atendimento.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <FileCheck className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Anexe catálogos, fichas técnicas, licenças ou certificados de qualidade que auxiliem na avaliação.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Clock className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Informamos o status da análise por e-mail em até <strong>5 dias úteis</strong>.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-primary/30 transition-colors">
                  <CardHeader className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle>Atendimento dedicado</CardTitle>
                        <CardDescription>
                          Suporte para dúvidas sobre necessidades técnicas ou logística.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 text-left">
                    <div className="flex gap-3">
                      <ShieldCheck className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Trabalhamos com fornecedores que atendam às normas de segurança, transporte e meio ambiente aplicáveis ao setor químico.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Mail className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Em caso de ajustes ou complementações, nossa equipe comercial orienta o reenvio de documentos diretamente pelo e-mail utilizado no primeiro contato.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Phone className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Ligue para <a className="text-primary font-semibold" href="tel:08005917215">0800 591 7215</a> para tratativas urgentes sobre cotações em andamento.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section id="cadastro" className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto space-y-12">
              <div className="space-y-4 text-center">
                <h2 className="text-3xl font-bold text-foreground">
                  Cadastro e homologação de fornecedores
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Para garantir a segurança operacional e a rastreabilidade de nossa cadeia, solicitamos que transportadores e fabricantes enviem a documentação listada abaixo. Todos os arquivos podem ser encaminhados em formato PDF ou via link compartilhado.
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-2">
                <Card className="border-2 hover:border-primary/30 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Truck className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle>Transportadoras</CardTitle>
                        <CardDescription>
                          Especialistas em cargas químicas e granel.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-outside space-y-3 pl-5 text-sm text-muted-foreground">
                      <li>Contrato social e documentos dos responsáveis legais atualizados.</li>
                      <li>Licenças de operação e registros da ANTT/ANTT-TAC se aplicável.</li>
                      <li>Apólices de seguro vigentes (RCTR-C, RC-ambiental ou equivalentes).</li>
                      <li>Relação da frota disponível com ano/modelo e equipamentos de segurança embarcados.</li>
                      <li>Procedimentos de atendimento a emergências e certificações de treinamento da equipe.</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-primary/30 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <FlaskConical className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle>Fabricantes químicos</CardTitle>
                        <CardDescription>
                          Fornecedores de matérias-primas e especialidades.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-outside space-y-3 pl-5 text-sm text-muted-foreground">
                      <li>Certificações ISO, FISPQ, FDS ou equivalentes para cada produto ofertado.</li>
                      <li>Relatórios de controle de qualidade e rastreabilidade de lotes.</li>
                      <li>Políticas de sustentabilidade, descarte e tratamento de resíduos.</li>
                      <li>Capacidade produtiva mensal e lead time de fabricação.</li>
                      <li>Referências comerciais e clientes atendidos no segmento industrial.</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card className="border border-primary/30 bg-primary/5">
                <CardContent className="py-8">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-10">
                    <ShieldCheck className="w-12 h-12 text-primary" />
                    <div className="space-y-3 text-center md:text-left">
                      <h3 className="text-2xl font-semibold text-foreground">Envio da documentação</h3>
                      <p className="text-muted-foreground">
                        Centralize os arquivos e envie para <a className="text-primary font-semibold" href="mailto:fornecedores@quimical.com.br?subject=Documenta%C3%A7%C3%A3o%20para%20cadastro%20de%20fornecedor">fornecedores@quimical.com.br</a>. Informe um contato responsável, disponibilidade para auditorias e validade de cada documento.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto space-y-12">
              <div className="space-y-4 text-center">
                <h2 className="text-3xl font-bold text-foreground">
                  Fluxo de homologação
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Após o envio completo das informações, seguimos as etapas abaixo para validar o cadastro e liberar o início das operações.
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-3">
                <Card className="text-center border-2 hover:border-primary/30 transition-colors">
                  <CardContent className="pt-8 space-y-4">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <FileCheck className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Análise documental</h3>
                    <p className="text-sm text-muted-foreground">
                      Conferimos registros legais, certificações e histórico de atendimento para garantir conformidade regulatória.
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center border-2 hover:border-primary/30 transition-colors">
                  <CardContent className="pt-8 space-y-4">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <ShieldCheck className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Avaliação técnica</h3>
                    <p className="text-sm text-muted-foreground">
                      Nossos especialistas verificam capacidade operacional, aderência às normas de segurança e contingência.
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center border-2 hover:border-primary/30 transition-colors">
                  <CardContent className="pt-8 space-y-4">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Clock className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Aprovação e onboarding</h3>
                    <p className="text-sm text-muted-foreground">
                      Após a aprovação, enviamos o acordo comercial e orientações para integração aos nossos fluxos logísticos e fiscais.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <Card className="max-w-4xl mx-auto border-2">
              <CardContent className="py-10 px-6 md:px-12">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Mail className="w-7 h-7" />
                  </div>
                  <div className="space-y-4 text-center md:text-left">
                    <h3 className="text-2xl font-semibold text-foreground">Fale com nossa equipe de suprimentos</h3>
                    <p className="text-muted-foreground">
                      Estamos disponíveis de segunda a sexta-feira, das 7h às 17h, para apoiar sua empresa no processo de homologação e nas demandas de fornecimento.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 justify-center md:justify-start">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4 text-primary" />
                        <a className="text-primary font-semibold" href="mailto:fornecedores@quimical.com.br">fornecedores@quimical.com.br</a>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-4 h-4 text-primary" />
                        <a className="text-primary font-semibold" href="tel:08005917215">0800 591 7215</a>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Fornecedores;
